import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  ViewChild
}                    from '@angular/core';
import {FormBuilder} from '@angular/forms';

import {filter, map} from 'rxjs/operators';

import {Observable, Subscription}                                      from 'rxjs';
import {
  ApDynComponentComponent
}                                                                      from '../../../../ap-dyncomponent/ap-dyncomponent.component';
import {
  ApDynGridColumnConfigBase,
  ApDynGridColumnHideConfig
}                                                                      from 'src/app/ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {
  ApDynGridPagerConfig
}                                                                      from '../../../../ap-dyngrids/config/ap-dyn-grid-pager-config';
import {
  UserContactsStore
}                                                                      from '../../../../stores/base-data/user.contacts.store';
import {
  CampaignYearStore
}                                                                      from '../../../../stores/login/campaignyear.store';
import {FormStore}                                                     from '../../../../stores/layout/form.store';
import {ApGridPagerType, IGridData, IGridPagerData, IWizardChoiceData} from '../../../../ap-interface';
import {
  ApDynGridPropertyColumnConfig
}                                                                      from '../../../../ap-dyngrids/config/ap-dyn-grid-property-column-config';
import {
  ApDynGridStatusColumnConfig
}                                                                      from '../../../../ap-dyngrids/config/ap-dyn-grid-status-column-config';
import {
  TranslationStore
}                                                                      from '../../../../stores/translation/translation.store';
import {
  ApDynGridEditColumnConfig
}                                                                      from '../../../../ap-dyngrids/config/ap-dyn-grid-edit-column-config';
import {
  ApUserContactsEditComponent
}                                                                      from '../../../../entry-components/ap-basedata-entry-components/ap-user-contacts-edit.component';
import {ObjectFactory}                                                 from 'ts-tooling';
import IUserContacts = Data.BaseData.IUserContacts;

interface IUserContactsGridData extends IGridData<IUserContacts> {
  Status: number;
  StatusClass: string;
  StatusDesc: string;
}

enum UserContactEdits {
  IsActive = 0,
  Remove = 1
}

enum UserContactEditGridActions {
  ACTIVE = 1,
  INACTIVE = 0
}

/**
 * Component for the user_contacts overview
 */
@Component({
  selector: 'ap-user-contacts',
  template: `
    <ap-dyncomponent [columns]="columns"
                     [items]="items$ | async"
                     [loading$]="userContactsLoading$"
                     [pager]="pager"
                     [groupable]="true"
                     [canSearch]="true"
                     (create)="onCreate()"
                     [canCreate]="true"
                     (edit)="onEdit($event)">
    </ap-dyncomponent>`,
  changeDetection: ChangeDetectionStrategy.OnPush
})

/**
 * User-Contacts overview component.
 */
export class ApUserContactsOverviewComponent implements OnInit, AfterContentInit, OnDestroy {
  @ViewChild(ApDynComponentComponent, {static: true}) dynComponent;
  public userContactsLoading$: Observable<boolean>;
  public items$: Observable<IUserContactsGridData[]>;
  public columns: ApDynGridColumnConfigBase[];
  public pager: ApDynGridPagerConfig;
  private _subscriptions: Subscription[] = [];
  private _listMassEditing = [];
  private _applyClicked = new EventEmitter<any>();

  constructor(public userContactsStore: UserContactsStore,
              private fb: FormBuilder,
              private formStore: FormStore,
              private translationStore: TranslationStore,
              private campaignYearStore: CampaignYearStore) {
    this._subscriptions.push(this._applyClicked.subscribe((e) => this._onApplyClicked(e)));
    this.userContactsLoading$ = this.userContactsStore.Listen(s => s.loading);
    this.items$ = this.userContactsStore.Listen(s => s.data).pipe(
      filter(d => !!d),
      map(u => u.filter(_ => !_.DeletedAt)),
      map(d => d.Convert<IUserContactsGridData>(userContact => ({
        Id: userContact.Id,
        SourceItem: userContact,
        Status: userContact.IsActive ? 1 : -1,
        StatusClass: userContact.IsActive ? 'ap-status-green' : 'ap-status-red',
        StatusDesc: userContact.IsActive ? 'Global__Active' : 'Global__Inactive',
      }))),
    );
  }

  ngAfterContentInit(): void {
    this.fillWizardChoiceData();
    this.gridBuilder();
  }

  ngOnInit(): void {
    this._subscriptions.push(this.campaignYearStore.Listen(s => s.selectedYear).subscribe(() => {
      this.userContactsStore.loadUserContacts();
    }));
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach(s => s.unsubscribe());
  }

  onCreate(): void {
    setTimeout(() => this.formStore.openForm({component: ApUserContactsEditComponent, data: null}), 1);
  }

  onEdit(dataItem): void {
    setTimeout(() => this.formStore.openForm({component: ApUserContactsEditComponent, data: dataItem.SourceItem}), 1);
  }

  /**
   * Build grid columns
   */
  gridBuilder(): void {
    this.columns = [
      new ApDynGridStatusColumnConfig({
        field: 'Status', showHeaderTitle: false,
        class: 'StatusClass', description: 'StatusDesc', filterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'SourceItem.Firstname', title: 'Global__Forename',
        filterable: true,
        headerFilterable: true, sortIndex: 0
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'SourceItem.Lastname', title: 'Global__Name',
        filterable: true,
        headerFilterable: true,
        hide: new ApDynGridColumnHideConfig({
          mapHide: true
        })
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'SourceItem.FarmName', title: 'Global_Organization',
        filterable: true,
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'SourceItem.MailAddress', title: 'Global__Email',
        filterable: true,
        headerFilterable: true
      }),
      new ApDynGridEditColumnConfig()
    ];
    this.pager = new ApDynGridPagerConfig({
      listMassEditing: this._listMassEditing,
      apply: this._applyClicked
    });
  }

  fillWizardChoiceData(): void {
    const state = {
      id: UserContactEdits.IsActive,
      name: this.translationStore.FindTranslationForSelectedLanguage('Global__Status'),
      type: ApGridPagerType.DropDown,
      values: [
        {
          id: UserContactEditGridActions.ACTIVE,
          label: this.translationStore.FindTranslationForSelectedLanguage('Global__Active')
        },
        {
          id: UserContactEditGridActions.INACTIVE,
          label: this.translationStore.FindTranslationForSelectedLanguage('Global__Inactive')
        }
      ]
    } as IWizardChoiceData;
    this._listMassEditing.push(state);
    const remove = {
      id: UserContactEdits.Remove,
      name: this.translationStore.FindTranslationForSelectedLanguage('Global_Remove'),
      type: ApGridPagerType.None,
      values: null,
    } as IWizardChoiceData;
    this._listMassEditing.push(remove);
  }

  private _onApplyClicked(data: IGridPagerData): void {
    const rows = this.dynComponent.getSelectedItems();
    rows.forEach(row => {
      const userContact = ObjectFactory.Copy(row['SourceItem'] as IUserContacts);
      if (!userContact) {
        return;
      }
      switch (data.object.id) {
        case UserContactEdits.IsActive:
          userContact.IsActive = (data.value.id === UserContactEditGridActions.ACTIVE);
          this.userContactsStore.updateUserContact(userContact);
          break;
        case UserContactEdits.Remove:
          this.userContactsStore.deleteUserContact(userContact);
          break;
      }
    });
  }
}
